/* eslint-disable max-lines */
import {
  FieldAutocomplete,
  FieldNumber,
  FormSchemaOptions,
  FormSchemaReturn,
} from '@explorer/forms';
import {
  ClientRoles,
  MultiTenantCapabilitiesCustomerTypes,
} from '@explorer/forms/types';
import { formatDate, isTodayBeforeGivenDate } from '@explorer/helpers';
import {
  ControlClientInfoResponse,
  ControlControlComponent,
} from '@explorer/open-api-client';
import * as yup from 'yup';

import { clientRoleOptions } from './constants';
import {
  Modules,
  SourceSystem,
  SUPPORTED_MODULES,
  SupportedModules,
  SupportedSystems,
} from './types';

const allowedTypes = Object.values(MultiTenantCapabilitiesCustomerTypes);
const baseCustomerType = yup
  .mixed<MultiTenantCapabilitiesCustomerTypes>()
  .oneOf(allowedTypes, 'Invalid customer type');

const emptyMtcDetails = {
  customer_type: '',
  notes: '',
};

/**
 * Form Validation Schema
 */
export const validationSchema = yup.object().shape({
  count_max_lite: yup.number().required(),
  count_max_extended: yup
    .number()
    .integer()
    .required()
    .when('role', {
      is: ClientRoles.PORTAL_FREEMIUM,
      then: yup.number().integer().max(0),
    })
    .when('role', {
      is: ClientRoles.PORTAL_POLICYHOLDER,
      then: yup.number().integer().min(1),
    })
    .when('role', {
      is: ClientRoles.PORTAL_PREMIUM,
      then: yup.number().integer().min(1),
    }),
  role: yup.mixed<ClientRoles>().oneOf(Object.values(ClientRoles)).required(),
  control_components: yup.object({
    MDR: yup.boolean(),
    SAT: yup.boolean(),
    MTC: yup.boolean(),
  }),
  mtc_details: yup
    .object({
      customer_type: baseCustomerType,
      notes: yup.string(),
    })
    .when('control_components.MTC', {
      is: true,
      then: (s) =>
        s.shape({
          customer_type: baseCustomerType.required('Select a customer type'),
          notes: yup.string().required('Required when MTC is enabled'),
        }),
    }),
});

/**
 * Form Values
 */
export type FormValues = {
  count_max_lite: number;
  count_max_extended: number;
  role: ClientRoles;
  control_components: Record<SupportedModules, boolean>;
  mtc_details: {
    customer_type: MultiTenantCapabilitiesCustomerTypes | string;
    notes: string;
  };
};

/**
 * Form Schema
 */
export const initializeFormSchema = ({
  formId,
  formik,
}: FormSchemaOptions): FormSchemaReturn<FormValues> => ({
  count_max_lite: {
    formId,
    formik,
    name: 'count_max_lite',
    renderField: (props) => (
      <FieldNumber {...props} label={{ id: 'forms-presets.max-lite' }} />
    ),
  },
  count_max_extended: {
    formId,
    formik,
    name: 'count_max_extended',
    renderField: (props) => (
      <FieldNumber
        {...props}
        label={{ id: 'forms-presets.max-extended' }}
        disabled={formik.values.role === ClientRoles.PORTAL_FREEMIUM}
      />
    ),
  },
  role: {
    formId,
    formik,
    name: 'role',
    renderField: (props) => (
      <FieldAutocomplete
        {...props}
        label={{ id: 'forms-presets.role' }}
        options={clientRoleOptions}
      />
    ),
  },
  control_components: {
    formId,
    formik,
    name: 'control_components',
    renderField: () => null,
  },
  mtc_details: {
    formId,
    formik,
    name: 'mtc_details',
    renderField: () => null,
  },
});

export const getInitialValues = (
  activeClient: ControlClientInfoResponse,
  modules: Modules,
): FormValues => {
  let mtc_details: FormValues['mtc_details'];
  try {
    const mtcDetailsString =
      activeClient?.client?.active_components?.MTC?.[0]?.component_json;
    if (mtcDetailsString) {
      const parsing = JSON.parse(mtcDetailsString);
      mtc_details = {
        customer_type: parsing.customer_type ?? '',
        notes: parsing.notes ?? '',
      };
    } else {
      mtc_details = emptyMtcDetails;
    }
  } catch (e) {
    mtc_details = emptyMtcDetails;
  }

  return {
    role:
      (activeClient?.client?.role as ClientRoles) ??
      ClientRoles.PORTAL_FREEMIUM,
    count_max_lite: activeClient?.client?.quotas?.lite_quota ?? 0,
    count_max_extended: activeClient?.client?.quotas?.extended_quota ?? 0,
    control_components: modules.overrides,
    mtc_details,
  };
};

/**
 * This takes the response from the BE client endpoint and parses the returned data into
 * "Supported" modules.
 *
 * @param components The list of "component" modules that are enabled for the client.
 * @returns metadata about the modules in a record with the module name as the key.
 */
export const getModules = (client?: {
  active_components?: Record<string, Array<ControlControlComponent>>;
  pre_purchase_components?: Record<string, Array<ControlControlComponent>>;
}): Modules => {
  const {
    active_components: activeComponents,
    pre_purchase_components: prePurchaseComponents,
  } = client ?? {};

  const modules = SUPPORTED_MODULES.reduce<Modules>(
    (acc, module) => {
      const sourceSystems = activeComponents?.[module] || [];
      const modulesForCurrent = sourceSystems
        .map<SourceSystem>((sourceSystem) =>
          getSourceSystemMetaData(sourceSystem, acc.overrides),
        )
        .filter(Boolean); // Filter out null values

      if (modulesForCurrent.length > 0) {
        acc.modules[module] = modulesForCurrent;
      }
      return acc;
    },
    {
      overrides: {
        MDR: false,
        SAT: false,
        MTC: false,
      },
      modules: { MDR: [], SAT: [], MTC: [] },
    },
  );

  const modulesWithPrepurchase = SUPPORTED_MODULES.reduce<Modules>(
    (acc, module) => {
      const modulePrepurchaseComponent = prePurchaseComponents?.[module];

      if (!modulePrepurchaseComponent?.length) {
        return acc;
      }

      const prepurchaseInfo = getPrepurchaseModuleInfo(
        modulePrepurchaseComponent,
      );

      if (prepurchaseInfo) {
        acc.prepurchasedModules[module] = prepurchaseInfo;
      }

      return acc;
    },
    { prepurchasedModules: { MDR: null, SAT: null, MTC: null }, ...modules },
  );

  return modulesWithPrepurchase;
};

export const getPrepurchaseModuleInfo = (
  prepurchaseComponent: ControlControlComponent[],
) => {
  // Grab the first
  const { component_json } = prepurchaseComponent[0] ?? {};

  let prepurchaseInfo;
  try {
    prepurchaseInfo = JSON.parse(component_json ?? null);
  } catch {
    return undefined;
  }

  const {
    presented_price: price,
    insurance_security_sku: productSku,
    price_target_currency: currency,
  } = prepurchaseInfo ?? {};

  return {
    // Get some pricing info
    productSku,
    currency,
    price,
  };
};

/**
 * Maps the supported source systems and modules into the corresponding metadata for that source system.
 *
 * @param sourceSystem The source system where the pricing information comes from (Explorer overrides, Stripe, Hubspot, etc.)
 * @param overrides The supported overrides for the source system (MDR can be overriden in explorer to give access even if Stripe/Hubspot have expired)
 * @returns The SourceSystem metadata
 */
export const getSourceSystemMetaData = (
  sourceSystem: ControlControlComponent,
  overrides: Record<SupportedModules, boolean>,
): SourceSystem | null => {
  const { source_system, is_trial, end_date, component_name } = sourceSystem;
  switch (source_system) {
    case SupportedSystems.EXPLORER:
      // eslint-disable-next-line no-param-reassign
      overrides[component_name] = true;

      return {
        sourceSystems: SupportedSystems.EXPLORER,
        featureSet: { isEnabled: true },
      };

    case SupportedSystems.STRIPE:
    case SupportedSystems.HUBSPOT:
    case SupportedSystems.SALESFORCE:
      return {
        sourceSystems: source_system,
        featureSet: {
          isTrial: is_trial,
          isExpired: !end_date ? false : !isTodayBeforeGivenDate(end_date),
          endDate: !end_date ? null : formatDate(end_date),
        },
      };
    default:
      return null;
  }
};
